// ===========================================
// New Venue Form (inline)
// ===========================================
// Inline form for creating a reusable external EventVenue from within the
// event editor's venue selector. On save it POSTs the venue and calls
// onCreated(venue) so the selector can immediately select it.
//
// Reuses the LocationsPage structured-address pattern (country autocomplete
// with dynamic state/postcode labels).

import { useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useCreateEventVenue } from '../../hooks/useEventVenues';
import { COUNTRIES, getCountry } from '../../constants/countries';
import type { EventVenue } from '../../api/events.api';

interface NewVenueFormProps {
  onCreated: (venue: EventVenue) => void;
  onCancel: () => void;
}

interface VenueFormState {
  name: string;
  streetAddress: string;
  unit: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  phone: string;
  capacity: string;
}

const defaultState: VenueFormState = {
  name: '',
  streetAddress: '',
  unit: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'AU',
  phone: '',
  capacity: '',
};

export default function NewVenueForm({ onCreated, onCancel }: NewVenueFormProps) {
  const [form, setForm] = useState<VenueFormState>(defaultState);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateEventVenue();

  const country = getCountry(form.country);
  const stateOptions = country?.states ?? [];

  const set = <K extends keyof VenueFormState>(key: K, value: VenueFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Venue name is required.');
      return;
    }
    setError(null);
    try {
      const venue = await createMutation.mutateAsync({
        name: form.name.trim(),
        streetAddress: form.streetAddress.trim() || null,
        unit: form.unit.trim() || null,
        suburb: form.suburb.trim() || null,
        state: form.state.trim() || null,
        postcode: form.postcode.trim() || null,
        country: form.country,
        phone: form.phone.trim() || null,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      });
      onCreated(venue);
    } catch {
      // The hook surfaces the error toast (incl. 409 duplicate name). Keep the
      // form open so the admin can fix the name and retry.
    }
  };

  const isSaving = createMutation.isPending;

  return (
    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
      <Stack spacing={2}>
        <TextField
          label="Venue Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
          fullWidth
          size="small"
          error={!!error}
          helperText={error ?? undefined}
          placeholder="e.g. Town Hall Concert Auditorium"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Unit / Suite"
            value={form.unit}
            onChange={(e) => set('unit', e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="Street Address"
            value={form.streetAddress}
            onChange={(e) => set('streetAddress', e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Suburb / City"
            value={form.suburb}
            onChange={(e) => set('suburb', e.target.value)}
            size="small"
            fullWidth
          />
          {stateOptions.length > 0 ? (
            <Autocomplete
              options={stateOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={stateOptions.find((s) => s.code === form.state) ?? null}
              onChange={(_, sel) => set('state', sel?.code ?? '')}
              renderInput={(params) => (
                <TextField {...params} label={country?.stateLabel ?? 'State'} size="small" />
              )}
              sx={{ minWidth: 200 }}
            />
          ) : (
            <TextField
              label={country?.stateLabel ?? 'State / Region'}
              value={form.state}
              onChange={(e) => set('state', e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={country?.postcodeLabel ?? 'Postcode'}
            value={form.postcode}
            onChange={(e) => set('postcode', e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          />
          <Autocomplete
            options={COUNTRIES}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            // disableClearable → value must be non-null; AU is always present.
            value={getCountry(form.country) ?? COUNTRIES[0]}
            onChange={(_, sel) =>
              setForm((prev) => ({
                ...prev,
                country: sel.code,
                // Clear state when country changes (codes differ per country).
                state: '',
              }))
            }
            renderInput={(params) => <TextField {...params} label="Country" size="small" />}
            sx={{ minWidth: 200 }}
            disableClearable
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Capacity"
            type="number"
            value={form.capacity}
            onChange={(e) => set('capacity', e.target.value)}
            size="small"
            inputProps={{ min: 0 }}
            sx={{ minWidth: 140 }}
            placeholder="Optional"
          />
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={isSaving} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={14} /> : undefined}
          >
            Save Venue
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
